import { useEffect, useState, type CSSProperties, type MouseEvent } from 'react';
import type { ApiClient, ApiError, Role } from '@aithora/shared';
import { sx } from '../../ui/designStyles';
import { useSession } from '../../session/SessionContext';
import { useQuery, type QueryState } from '../../useQuery';

export { sx };
export type Style = CSSProperties;

/** Load one route with the session's client (re-loads when the session or deps change). */
export function useApi<T>(load: (client: ApiClient, signal: AbortSignal) => Promise<T>, deps: unknown[] = [], enabled = true): QueryState<T> {
  const { client, session } = useSession();
  return useQuery<T>((signal) => load(client, signal), [client, session?.token, ...deps], { enabled });
}

/** A PROPOSED route answers 404 until the back end serves it; that is "not served", not an error. */
export function notServed(e: ApiError | null): boolean {
  return !!e && (e as { status?: number }).status === 404;
}

/** The console-root width the design lays the Guards grid out by (design pcW). */
export function useShellWidth(): number {
  const read = () => (typeof window !== 'undefined' ? window.innerWidth : 1400);
  const [w, setW] = useState(read);
  useEffect(() => {
    const on = () => setW(read());
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return w;
}

/** The design's anchors are links; ours are buttons or links that never navigate. */
export const pre = (e?: MouseEvent | { preventDefault?: () => void }) => {
  if (e && e.preventDefault) e.preventDefault();
};

export type PolicyRole = 'operator' | 'facility' | 'readonly';
export function policyRoleOf(role: Role | null): PolicyRole {
  if (role === 'SUPER_ADMIN' || role === 'OPERATOR') return 'operator';
  if (role === 'ADMIN') return 'facility';
  return 'readonly';
}

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

/** "2m ago", "3h ago", "2 days ago". */
export function ago(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '–';
  const d = Math.max(0, now - new Date(iso).getTime());
  if (d < HOUR) return `${Math.max(1, Math.round(d / MIN))}m ago`;
  if (d < DAY) return `${Math.round(d / HOUR)}h ago`;
  const n = Math.round(d / DAY);
  return n === 1 ? '1 day ago' : `${n} days ago`;
}

/** The short form the playbook history uses: "12m", "1h", "3h", "2d". */
export function agoShort(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '–';
  const d = Math.max(0, now - new Date(iso).getTime());
  if (d < HOUR) return `${Math.max(1, Math.round(d / MIN))}m`;
  if (d < DAY) return `${Math.round(d / HOUR)}h`;
  return `${Math.round(d / DAY)}d`;
}

/** "9:12 AM" */
export function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

/** "Today 9:12 AM", "Yesterday 4:05 PM", "Sep 21 4:05 PM". */
export function dayClock(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '–';
  const t = new Date(iso);
  const n = new Date(now);
  const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (sameDay(t, n)) return `Today ${clock(iso)}`;
  const y = new Date(now - DAY);
  if (sameDay(t, y)) return `Yesterday ${clock(iso)}`;
  return `${t.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${clock(iso)}`;
}

/** Milliseconds as the playbook form writes them: "5m", "1h", "30s", "0m". */
export function msShort(ms: number | null | undefined): string {
  if (ms == null) return '–';
  if (ms === 0) return '0m';
  if (ms % HOUR === 0) return `${ms / HOUR}h`;
  if (ms % MIN === 0) return `${ms / MIN}m`;
  return `${Math.round(ms / 1000)}s`;
}

/** "5m", "1h", "30s" back to milliseconds; null when it does not read. */
export function parseDuration(s: string): number | null {
  const m = /^\s*(\d+(?:\.\d+)?)\s*(s|m|h|d)?\s*$/i.exec(s);
  if (!m) return null;
  const n = Number(m[1]);
  const unit = (m[2] ?? 'm').toLowerCase();
  return Math.round(n * (unit === 's' ? 1000 : unit === 'm' ? MIN : unit === 'h' ? HOUR : DAY));
}

/** The design's switch (sw(on), L26425). */
export function switchStyles(on: boolean): { wrap: string; knob: string } {
  return {
    wrap:
      'display:inline-flex;align-items:center;width:42px;height:24px;flex:0 0 42px;border-radius:14px;padding:2px;box-sizing:border-box;text-decoration:none;white-space:nowrap;' +
      (on ? 'background:#1F6B52;justify-content:flex-end;' : 'background:rgba(34,39,46,.22);justify-content:flex-start;'),
    knob: 'display:block;width:20px;height:20px;border-radius:50%;background:#fff;box-shadow:0 1px 3px rgba(34,39,46,.28);',
  };
}

/** The design's 7-day bars (bars(arr, color, h), L26088), animation dropped (motion.css runs growH). */
export function barStyles(arr: readonly number[], color: string, h: number): string[] {
  const mx = Math.max(1, ...arr);
  return arr.map(
    (x, i) =>
      'display:block;width:5px;border-radius:1.5px;height:' + Math.max(2, Math.round((x / mx) * h)) + 'px;background:' + (x ? color : 'rgba(34,39,46,.14)') +
      ';transform-origin:bottom;animation:growH .5s cubic-bezier(.22,.61,.36,1) ' + i * 40 + 'ms backwards;',
  );
}

export const ROLE_WORD: Partial<Record<string, string>> = {
  ADMIN: 'Facility Admin', SUPER_ADMIN: 'Super Admin', OPERATOR: 'Operator', DEVELOPER: 'Dev', PARTNER: 'Partner', ADMIN_TOKEN: 'Aithora',
};

/** The building's own roles: a customer may see these people's names. */
const CUSTOMER_ACTOR_ROLES = new Set(['ADMIN', 'CAREGIVER', 'STAFF']);

/**
 * Who made a change, in words this viewer may see. A Facility Admin (pr 'facility') sees
 * "Aithora" for anyone who is not one of their building's own staff, never a team
 * member's name (QA-09); Aithora's team sees the name, or the role when there is none.
 */
export function actorWord(pr: PolicyRole, name: string | null | undefined, role: string | null | undefined, fallback: string | null = 'Aithora'): string | null {
  if (pr === 'facility' && !(role && CUSTOMER_ACTOR_ROLES.has(role))) return name || role ? 'Aithora' : fallback;
  return name ?? (role ? ROLE_WORD[role] ?? role : fallback);
}
